import React, { useState, useEffect } from 'react';
import { adminApi } from '../../services/api';

/**
 * EHRProfileManager - Full CRUD for HealthcareUserEhrProfile
 * Surfaces ALL fields including complex JSON fields
 */
const EHRProfileManager = ({ userId, onUpdate }) => {
  const [profiles, setProfiles] = useState([]);
  const [memberships, setMemberships] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [editingId, setEditingId] = useState(null);
  const [showAddForm, setShowAddForm] = useState(false);
  const [activeSection, setActiveSection] = useState('basic'); // basic, identifiers, assignments, sync, features
  const [formData, setFormData] = useState({
    membership_id: '',
    ehr_system_name: '',
    ehr_identifiers: {},
    ehr_assignments: {},
    sync_config: {},
    feature_flags: {},
    ehr_provider_json: {}
  });

  useEffect(() => {
    fetchData();
  }, [userId]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [profilesRes, membershipsRes] = await Promise.all([
        adminApi.getUserEhrProfiles(userId),
        adminApi.getUserMemberships(userId)
      ]);

      setProfiles(profilesRes.data?.ehr_profiles || []);
      setMemberships(membershipsRes.data?.memberships || []);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to load EHR profiles');
    } finally {
      setLoading(false);
    }
  };

  const handleAdd = () => {
    setShowAddForm(true);
    setEditingId(null);
    setActiveSection('basic');
    setFormData({
      membership_id: '',
      ehr_system_name: '',
      ehr_identifiers: {},
      ehr_assignments: {},
      sync_config: {},
      feature_flags: {},
      ehr_provider_json: {}
    });
  };

  const handleEdit = (profile) => {
    setEditingId(profile.id);
    setShowAddForm(true);
    setActiveSection('basic');
    setFormData({
      membership_id: profile.membership?.id || '',
      ehr_system_name: profile.ehr_system_name || '',
      ehr_identifiers: profile.ehr_identifiers || {},
      ehr_assignments: profile.ehr_assignments || {},
      sync_config: profile.sync_config || {},
      feature_flags: profile.feature_flags || {},
      ehr_provider_json: profile.ehr_provider_json || {}
    });
  };

  const handleSave = async () => {
    try {
      if (editingId) {
        await adminApi.updateUserEhrProfile(userId, editingId, formData);
      } else {
        await adminApi.createUserEhrProfile(userId, formData);
      }
      setShowAddForm(false);
      setEditingId(null);
      fetchData();
      if (onUpdate) onUpdate();
    } catch (err) {
      alert(err.response?.data?.message || 'Failed to save EHR profile');
    }
  };

  const handleDelete = async (profileId) => {
    if (!window.confirm('Are you sure you want to delete this EHR profile?')) return;

    try {
      await adminApi.deleteUserEhrProfile(userId, profileId);
      fetchData();
      if (onUpdate) onUpdate();
    } catch (err) {
      alert(err.response?.data?.message || 'Failed to delete EHR profile');
    }
  };

  const updateNestedField = (parent, key, value) => {
    setFormData(prev => ({
      ...prev,
      [parent]: { ...prev[parent], [key]: value }
    }));
  };

  if (loading) {
    return (
      <div className="card">
        <div className="card-body text-center py-4">
          <div className="spinner-border text-primary" />
        </div>
      </div>
    );
  }

  return (
    <div className="card">
      <div className="card-header py-2 bg-light d-flex justify-content-between align-items-center">
        <h6 className="mb-0">
          <i className="fas fa-database me-2" />
          EHR Profiles
        </h6>
        {!showAddForm && (
          <button className="btn btn-sm btn-primary" onClick={handleAdd}>
            <i className="fas fa-plus me-1" />Add EHR Profile
          </button>
        )}
      </div>

      <div className="card-body p-2">
        {error && <div className="alert alert-danger small mb-2">{error}</div>}

        {showAddForm && (
          <div className="border rounded p-3 mb-3 bg-light">
            <h6 className="mb-3">{editingId ? 'Edit EHR Profile' : 'Add EHR Profile'}</h6>

            {/* Section Tabs */}
            <ul className="nav nav-pills nav-sm mb-3">
              <li className="nav-item">
                <button
                  className={`nav-link ${activeSection === 'basic' ? 'active' : ''}`}
                  onClick={() => setActiveSection('basic')}
                >
                  Basic
                </button>
              </li>
              <li className="nav-item">
                <button
                  className={`nav-link ${activeSection === 'identifiers' ? 'active' : ''}`}
                  onClick={() => setActiveSection('identifiers')}
                >
                  Identifiers
                </button>
              </li>
              <li className="nav-item">
                <button
                  className={`nav-link ${activeSection === 'assignments' ? 'active' : ''}`}
                  onClick={() => setActiveSection('assignments')}
                >
                  Assignments
                </button>
              </li>
              <li className="nav-item">
                <button
                  className={`nav-link ${activeSection === 'sync' ? 'active' : ''}`}
                  onClick={() => setActiveSection('sync')}
                >
                  Sync Config
                </button>
              </li>
              <li className="nav-item">
                <button
                  className={`nav-link ${activeSection === 'features' ? 'active' : ''}`}
                  onClick={() => setActiveSection('features')}
                >
                  Features
                </button>
              </li>
            </ul>

            {/* Basic Section */}
            {activeSection === 'basic' && (
              <div className="row">
                <div className="col-md-6 mb-3">
                  <label className="form-label small mb-1">Membership</label>
                  <select
                    className="form-select form-select-sm"
                    value={formData.membership_id}
                    onChange={e => setFormData({ ...formData, membership_id: e.target.value })}
                  >
                    <option value="">No Membership Link</option>
                    {memberships.map(m => (
                      <option key={m.id} value={m.id}>
                        {m.health_system?.name} - {m.role?.name}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="col-md-6 mb-3">
                  <label className="form-label small mb-1">EHR System Name *</label>
                  <input
                    type="text"
                    className="form-control form-control-sm"
                    value={formData.ehr_system_name}
                    onChange={e => setFormData({ ...formData, ehr_system_name: e.target.value })}
                    placeholder="Epic, Cerner, Athena, etc."
                    required
                  />
                </div>
              </div>
            )}

            {/* Identifiers Section */}
            {activeSection === 'identifiers' && (
              <div className="row">
                <div className="col-12 mb-2">
                  <small className="text-muted">EHR Identifiers (Provider/User IDs, NPI, DEA, etc.)</small>
                </div>
                {['ehr_provider_id', 'ehr_user_id', 'npi_in_ehr', 'dea_number_in_ehr'].map(key => (
                  <div key={key} className="col-md-6 mb-3">
                    <label className="form-label small mb-1">{key.replace(/_/g, ' ').toUpperCase()}</label>
                    <input
                      type="text"
                      className="form-control form-control-sm"
                      value={formData.ehr_identifiers[key] || ''}
                      onChange={e => updateNestedField('ehr_identifiers', key, e.target.value)}
                      placeholder={`Enter ${key}`}
                    />
                  </div>
                ))}
                <div className="col-12 mb-3">
                  <label className="form-label small mb-1">License Numbers (JSON)</label>
                  <textarea
                    className="form-control form-control-sm"
                    rows={2}
                    value={JSON.stringify(formData.ehr_identifiers.license_numbers || {}, null, 2)}
                    onChange={e => {
                      try {
                        updateNestedField('ehr_identifiers', 'license_numbers', JSON.parse(e.target.value));
                      } catch {
                        // Silently ignore JSON parse errors while user is typing
                      }
                    }}
                    placeholder='{"state": "CA", "number": "A12345"}'
                  />
                </div>
              </div>
            )}

            {/* Assignments Section */}
            {activeSection === 'assignments' && (
              <div className="row">
                <div className="col-12 mb-2">
                  <small className="text-muted">Department, Clinic, Schedule Assignments</small>
                </div>
                {['department_id', 'department_name', 'clinic_ids', 'schedule_ids', 'roles', 'permissions', 'resource_ids'].map(key => (
                  <div key={key} className="col-md-6 mb-3">
                    <label className="form-label small mb-1">{key.replace(/_/g, ' ').toUpperCase()}</label>
                    <input
                      type="text"
                      className="form-control form-control-sm"
                      value={formData.ehr_assignments[key] || ''}
                      onChange={e => updateNestedField('ehr_assignments', key, e.target.value)}
                      placeholder={`Enter ${key}`}
                    />
                  </div>
                ))}
              </div>
            )}

            {/* Sync Config Section */}
            {activeSection === 'sync' && (
              <div className="row">
                <div className="col-12 mb-2">
                  <small className="text-muted">Sync Status and Configuration</small>
                </div>
                {['sync_status', 'sync_frequency', 'last_sync_status', 'last_sync_error', 'sync_retry_count', 'next_sync_scheduled'].map(key => (
                  <div key={key} className="col-md-6 mb-3">
                    <label className="form-label small mb-1">{key.replace(/_/g, ' ').toUpperCase()}</label>
                    <input
                      type="text"
                      className="form-control form-control-sm"
                      value={formData.sync_config[key] || ''}
                      onChange={e => updateNestedField('sync_config', key, e.target.value)}
                      placeholder={`Enter ${key}`}
                    />
                  </div>
                ))}
              </div>
            )}

            {/* Features Section */}
            {activeSection === 'features' && (
              <div className="row">
                <div className="col-12 mb-2">
                  <small className="text-muted">EHR Features and Permissions</small>
                </div>
                {['ehr_features_enabled', 'api_access_level'].map(key => (
                  <div key={key} className="col-md-6 mb-3">
                    <label className="form-label small mb-1">{key.replace(/_/g, ' ').toUpperCase()}</label>
                    <input
                      type="text"
                      className="form-control form-control-sm"
                      value={formData.feature_flags[key] || ''}
                      onChange={e => updateNestedField('feature_flags', key, e.target.value)}
                      placeholder={`Enter ${key}`}
                    />
                  </div>
                ))}
                {['can_prescribe', 'can_order_labs', 'can_view_phi', 'can_modify_records'].map(key => (
                  <div key={key} className="col-md-6 mb-3">
                    <div className="form-check">
                      <input
                        type="checkbox"
                        className="form-check-input"
                        checked={formData.feature_flags[key] || false}
                        onChange={e => updateNestedField('feature_flags', key, e.target.checked)}
                      />
                      <label className="form-check-label small">{key.replace(/_/g, ' ').toUpperCase()}</label>
                    </div>
                  </div>
                ))}
              </div>
            )}

            <div className="btn-group btn-group-sm mt-3">
              <button className="btn btn-success" onClick={handleSave}>
                <i className="fas fa-check me-1" />
                {editingId ? 'Update' : 'Create'}
              </button>
              <button className="btn btn-secondary" onClick={() => setShowAddForm(false)}>
                <i className="fas fa-times me-1" />Cancel
              </button>
            </div>
          </div>
        )}

        {profiles.length === 0 ? (
          <div className="text-center text-muted small py-3">
            <i className="fas fa-database me-1" />
            No EHR profiles configured
          </div>
        ) : (
          <div className="table-responsive">
            <table className="table table-sm table-hover mb-0">
              <thead className="table-light">
                <tr>
                  <th>EHR System</th>
                  <th>Membership</th>
                  <th>Provider ID</th>
                  <th>Sync Status</th>
                  <th>Last Synced</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {profiles.map(p => (
                  <tr key={p.id}>
                    <td><strong>{p.ehr_system_name}</strong></td>
                    <td>
                      {p.membership?.health_system?.name || '-'}
                      <div className="small text-muted">{p.membership?.role?.name}</div>
                    </td>
                    <td>{p.ehr_identifiers?.ehr_provider_id || '-'}</td>
                    <td>
                      <span className={`badge bg-${p.sync_config?.sync_status === 'active' ? 'success' : 'secondary'}`}>
                        {p.sync_config?.sync_status || 'inactive'}
                      </span>
                    </td>
                    <td className="small">
                      {p.last_synced_at ? new Date(p.last_synced_at).toLocaleString() : 'Never'}
                    </td>
                    <td>
                      <div className="btn-group btn-group-sm">
                        <button
                          className="btn btn-outline-primary"
                          onClick={() => handleEdit(p)}
                          title="Edit"
                        >
                          <i className="fas fa-edit" />
                        </button>
                        <button
                          className="btn btn-outline-danger"
                          onClick={() => handleDelete(p.id)}
                          title="Delete"
                        >
                          <i className="fas fa-trash" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default EHRProfileManager;
